import type { InvoiceStatus } from '../api/platform.js';

/**
 * Invoice-status pill (Phase D-2). Sibling of StatusBadge (subscription
 * statuses) — kept separate because the two enums are unrelated.
 *
 * Tailwind palette note (apps/web/tailwind.config.js): indigo/amber/emerald are
 * SINGLE-hex tokens (bare token or token/opacity — NOT `indigo-500`); slate/red
 * are default scales (use `-NNN`). The classes below respect that split — a
 * missing utility renders an invisible pill that jsdom tests can't catch.
 *
 * Status → colour (locked decision 2026-06-08):
 *   draft=slate · issued=indigo · paid=emerald · overdue=amber · voided=red
 */
const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  paid: 'Paid',
  overdue: 'Overdue',
  voided: 'Voided',
};

const STATUS_CLASSES: Record<InvoiceStatus, string> = {
  draft: 'bg-slate-500/10 text-slate-600 border-slate-400/40',
  issued: 'bg-indigo/10 text-indigo border-indigo/30',
  paid: 'bg-emerald/10 text-emerald border-emerald/30',
  overdue: 'bg-amber/10 text-amber border-amber/30',
  voided: 'bg-red-600/10 text-red-600 border-red-600/30',
};

export function InvoiceStatusBadge({
  status,
  size = 'md',
}: {
  status: InvoiceStatus;
  size?: 'sm' | 'md';
}): JSX.Element {
  const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-0.5';
  const colorClasses = STATUS_CLASSES[status] ?? STATUS_CLASSES.draft;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${sizeClasses} ${colorClasses}`}
      aria-label={`Invoice status: ${STATUS_LABELS[status]}`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}
